import api from '../api';

export interface NotificationType {
  INFO: string;
  WARNING: string;
  ERROR: string;
  SUCCESS: string;
  TIME_ENTRY: string;
  OVERTIME_ALERT: string;
  ABSENCE_REQUEST: string;
  SCHEDULE_CHANGE: string;
  REPORT_READY: string;
  SYSTEM_ALERT: string;
  BIRTHDAY: string;
  ANNIVERSARY: string;
}

export interface NotificationChannel {
  EMAIL: string;
  PUSH: string;
  SMS: string;
  IN_APP: string;
}

export interface NotificationStatus {
  PENDING: string;
  SENT: string;
  READ: string;
  FAILED: string;
}

export interface Notification {
  id: string;
  companyId: string;
  title: string;
  message: string;
  type: string;
  channel: string;
  recipientId: string | null;
  recipientEmail: string | null;
  status: string;
  sentAt: Date | null;
  readAt: Date | null;
  data: any;
  createdAt: Date;
  updatedAt: Date;
  createdById: string | null;
  createdBy?: {
    id: string;
    name: string;
    email: string;
  } | null;
  employee?: {
    id: string;
    name: string;
    dni: string;
  } | null;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type: string;
  channel?: string;
  recipientId?: string;
  recipientEmail?: string;
  data?: any;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  total: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  meta?: {
    unreadCount?: number;
    total?: number;
  };
}

export const notificationsApi = {
  /**
   * Obtener todas las notificaciones
   * @param includeRead Si es true, incluye las notificaciones leídas
   */
  async getNotifications(includeRead = false): Promise<ApiResponse<NotificationsResponse>> {
    const params = includeRead ? { includeRead: 'true' } : {};
    const response = await api.get<NotificationsResponse>('/api/notifications', params);
    console.log('📋 notificationsApi.getNotifications() - Raw response:', response);
    console.log('📋 notificationsApi.getNotifications() - response.success:', response.success);
    console.log('📋 notificationsApi.getNotifications() - response.data:', response.data);
    return response;
  },

  async getUnreadCount(): Promise<{ unreadCount: number }> {
    const response = await api.get<{ unreadCount: number }>('/api/notifications/unread-count');

    if (typeof response === 'object' && response !== null && 'data' in response && response.data && 'unreadCount' in response.data) {
      return response.data as { unreadCount: number };
    }

        return { unreadCount: 0 };
  },

  /**
   * Obtener una notificación por ID
   */
  async getNotificationById(id: string): Promise<ApiResponse<Notification>> {
    const response = await api.get<ApiResponse<Notification>>(`/api/notifications/${id}`);
    return response.data!;
  },

  /**
   * Crear una nueva notificación
   */
  async createNotification(data: CreateNotificationData): Promise<ApiResponse<Notification>> {
    const response = await api.post<ApiResponse<Notification>>('/api/notifications', data);
    return response.data!;
  },

  /**
   * Marcar una notificación como leída
   */
  async markAsRead(id: string): Promise<ApiResponse<Notification>> {
    const response = await api.put<ApiResponse<Notification>>(`/api/notifications/${id}/read`);
    return response.data!;
  },

  /**
   * Marcar todas las notificaciones como leídas
   */
  async markAllAsRead(): Promise<ApiResponse<void>> {
    const response = await api.put<ApiResponse<void>>('/api/notifications/read-all');
    return response.data!;
  },

  /**
   * Eliminar una notificación
   */
  async deleteNotification(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/api/notifications/${id}`);
    return response.data!;
  },

  /**
   * Eliminar todas las notificaciones leídas
   */
  async deleteReadNotifications(): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>('/api/notifications/read');
    return response.data!;
  },

  /**
   * Eliminar todas las notificaciones
   */
  async deleteAllNotifications(): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>('/api/notifications/all');
    return response.data!;
  },
};